/**
 * Wspolna logika widokow harmonogramu meczow klubu — pastylki kortow
 * i budowa osi czasu z plannera. Uzywana przez zakladki „Zaplanowane"
 * i „Rozpoczęte" oraz publiczna zakladke „Info ogólne".
 */

import type { ScheduleTile, ScheduleTimeline } from '../ui/ScheduleGrid';
import { toMinutes } from './courtPlanning';
import { shortTime } from './dashboardView';
import type { ApiMatchPlanner } from './courtTypes';

/**
 * Akcenty pastylki kortu — ten sam kort ma zawsze ten sam kolor, dobrany
 * deterministycznie z identyfikatora. Pelne literaly klas (Tailwind skanuje
 * kod statycznie), wzorzec `bg-accent-*\/12 + text-accent-*` jak w Avatarze.
 */
const COURT_ACCENTS = [
  'bg-accent-blue/12 text-accent-blue',
  'bg-accent-green/12 text-accent-green',
  'bg-accent-purple/12 text-accent-purple',
  'bg-accent-orange/12 text-accent-orange',
  'bg-accent-teal/12 text-accent-teal',
  'bg-accent-pink/12 text-accent-pink',
] as const;

/** Stabilny akcent dla kortu — prosty hash sumujacy kody znakow. */
export function courtAccent(courtId: string): string {
  let hash = 0;
  for (const char of courtId) {
    hash = (hash * 31 + char.codePointAt(0)!) | 0;
  }
  return COURT_ACCENTS[Math.abs(hash) % COURT_ACCENTS.length]!;
}

/** Minuty od polnocy na etykiete `HH:mm`. */
function minutesLabel(total: number): string {
  const hours = String(Math.floor(total / 60) % 24).padStart(2, '0');
  const minutes = String(total % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Pusta os — brak meczow z terminem i brak przerw tego dnia. */
const EMPTY_TIMELINE: ScheduleTimeline = {
  startMinutes: 0,
  endMinutes: 0,
  tiles: [],
  nowMinutes: null,
};

/** Kontekst czasu dla osi. */
export interface ScheduleTimelineOptions {
  /** Dzisiejsza data lokalna (yyyy-MM-dd) — do znacznika „TERAZ". */
  todayIso: string;
  /** Biezacy czas w minutach od polnocy. */
  nowMinutes: number;
}

/**
 * Buduje proporcjonalna os czasu z plannera: zakres to pelne godziny
 * obejmujace wszystkie mecze dnia i przerwy techniczne (takze poza godzinami
 * meczow), a kafelki sa nalozone dokladnie wedlug minut — mecz 9:00 na
 * 80 minut zajmuje pas 9:00–10:20. Mecze bez terminu albo bez kortu nie
 * wchodza na os (nie ma ich gdzie polozyc).
 */
export function buildScheduleTimeline(
  planner: ApiMatchPlanner,
  sideText: (ids: readonly string[]) => string,
  options: ScheduleTimelineOptions,
): ScheduleTimeline {
  const entries = planner.scheduledMatches.filter(
    (entry) => entry.match.scheduledTime && entry.match.courtId,
  );
  // Przerwy techniczne dnia — wchodza do zakresu osi nawet bez meczow wokol.
  const dayBlocks = planner.courts.flatMap((court) =>
    court.blocks
      .filter((block) => block.date === planner.date)
      .map((block) => ({
        courtId: court.id,
        start: toMinutes(block.startTime),
        end: toMinutes(block.endTime),
        startTime: block.startTime,
        endTime: block.endTime,
      })),
  );
  if (entries.length === 0 && dayBlocks.length === 0) return EMPTY_TIMELINE;

  const starts = entries.map((entry) => toMinutes(entry.match.scheduledTime!));
  const ends = entries.map((entry, index) => starts[index]! + entry.durationMinutes);
  const boundaries = [
    ...starts,
    ...ends,
    ...dayBlocks.flatMap((block) => [block.start, block.end]),
  ];

  const startMinutes = Math.floor(Math.min(...boundaries) / 60) * 60;
  const endMinutes = Math.ceil(Math.max(...boundaries) / 60) * 60;
  const isToday = planner.date === options.todayIso;

  const tiles: ScheduleTile[] = [];

  for (const [index, entry] of entries.entries()) {
    const start = starts[index]!;
    const end = ends[index]!;

    let emphasis: 'live' | 'soon' | 'none' = 'none';
    if (entry.match.status === 'IN_PROGRESS') emphasis = 'live';
    else if (
      isToday &&
      entry.match.status === 'SCHEDULED' &&
      start >= options.nowMinutes &&
      start - options.nowMinutes <= 60
    ) {
      emphasis = 'soon';
    }

    tiles.push({
      kind: 'match',
      courtId: entry.match.courtId!,
      startMinutes: start,
      endMinutes: end,
      home: sideText(entry.match.team1Ids),
      away: sideText(entry.match.team2Ids),
      note: `${minutesLabel(start)}–${minutesLabel(end)}`,
      emphasis,
      matchId: entry.match.id,
    });
  }

  for (const block of dayBlocks) {
    tiles.push({
      kind: 'break',
      courtId: block.courtId,
      startMinutes: block.start,
      endMinutes: block.end,
      label: `${shortTime(block.startTime) ?? ''}–${shortTime(block.endTime) ?? ''}`,
    });
  }

  return {
    startMinutes,
    endMinutes,
    tiles,
    nowMinutes:
      isToday && options.nowMinutes >= startMinutes && options.nowMinutes <= endMinutes
        ? options.nowMinutes
        : null,
  };
}
